import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { CheckboxField, SelectField, TextField } from '../components/Form';
import { PasswordField, PasswordStrength } from '../components/Password';
import { InlineError } from '../components/Feedback';
import { useAuth } from '../lib/auth';
import { signup } from '../lib/endpoints';
import { assessPassword, usePasswordPolicy } from '../lib/password';
import { errorMessage } from '../lib/api';
import { useToast } from '../lib/toast';

/** Countries the gateway is sold into; the API stores an ISO-3166 alpha-2 code. */
const COUNTRY_OPTIONS = [
  { value: 'GB', label: 'United Kingdom' },
  { value: 'IE', label: 'Ireland' },
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'IN', label: 'India' },
  { value: 'SG', label: 'Singapore' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function SignUpPage(): JSX.Element {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const policy = usePasswordPolicy();

  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [phoneNo, setPhoneNo] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isAuthenticated) return <Navigate to="/" replace />;

  const assessment = assessPassword(password, policy);
  const emailValid = EMAIL_PATTERN.test(email.trim());
  const confirmMismatch = confirm.length > 0 && confirm !== password;

  // Errors only appear after the first submit attempt, so the form is not hostile while typing.
  const fieldError = (invalid: boolean, message: string): string | null =>
    submitted && invalid ? message : null;

  const canSubmit =
    companyName.trim().length > 0 &&
    contactName.trim().length > 0 &&
    emailValid &&
    assessment.satisfied &&
    confirm === password &&
    acceptedTerms;

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
    setError(null);
    if (!canSubmit) {
      // The button stays enabled so a blocked submit can say what is wrong.
      setError(
        acceptedTerms
          ? 'Check the highlighted fields and try again.'
          : 'Please accept the terms of service to continue.',
      );
      return;
    }

    setPending(true);
    try {
      const response = await signup.create({
        companyName: companyName.trim(),
        contactName: contactName.trim(),
        email: email.trim(),
        password,
        phoneNo: phoneNo.trim() || null,
        countryCode: countryCode || null,
        acceptedTerms,
      });

      const address = response?.emailSentTo || email.trim();
      const hours = response?.linkValidForHours ?? 24;
      navigate(
        `/signup/check-email?email=${encodeURIComponent(address)}&hours=${encodeURIComponent(String(hours))}`,
        { replace: true },
      );
    } catch (cause) {
      const message = errorMessage(cause);
      setError(message);
      toast.error('Sign-up failed', message);
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthLayout
      title="Create your iERP Gateway account"
      subtitle="One account per company. You will be the first administrator."
      width="wide"
      footer={
        <>
          <span className="auth-link-text">Already have an account?</span>
          <Link to="/signin">Sign in</Link>
        </>
      }
      note="You will get an email to confirm this address before the account is created."
    >
      <form className="signin-form" onSubmit={onSubmit} noValidate>
        <div className="form-grid">
          <TextField
            label="Company name"
            value={companyName}
            onChange={setCompanyName}
            required
            autoComplete="organization"
            placeholder="Acme Trading Ltd"
            error={fieldError(companyName.trim() === '', 'Tell us the company name.')}
          />
          <TextField
            label="Your name"
            value={contactName}
            onChange={setContactName}
            required
            autoComplete="name"
            placeholder="Jane Bennet"
            error={fieldError(contactName.trim() === '', 'Tell us who you are.')}
          />
          <TextField
            label="Work email"
            type="email"
            value={email}
            onChange={setEmail}
            required
            autoComplete="email"
            placeholder="you@company.com"
            hint="We send the confirmation link here."
            error={fieldError(!emailValid, 'Enter a valid work email address.')}
          />
          <TextField
            label="Phone"
            value={phoneNo}
            onChange={setPhoneNo}
            autoComplete="tel"
            placeholder="Optional"
          />
          <SelectField
            label="Country"
            value={countryCode}
            onChange={setCountryCode}
            options={COUNTRY_OPTIONS}
            placeholder="Optional — not specified"
          />
          <PasswordField
            label="Password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
            error={fieldError(!assessment.satisfied, 'This password does not meet the policy.')}
          />
          <PasswordField
            label="Confirm password"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            required
            error={
              confirmMismatch || (submitted && confirm !== password)
                ? 'The two passwords do not match.'
                : null
            }
          />
        </div>

        <PasswordStrength password={password} policy={policy} />

        <CheckboxField
          label="I accept the iERP terms of service and privacy policy"
          checked={acceptedTerms}
          onChange={setAcceptedTerms}
          hint="Required. The account is created in your company's name."
        />

        <InlineError message={error} />

        <button type="submit" className="btn btn-primary btn-block" disabled={pending}>
          {pending ? 'Creating your account…' : 'Create account'}
        </button>
      </form>
    </AuthLayout>
  );
}
